import { cx } from '@emotion/css';
import type { InputProps } from '@flowus/common/components/input';
import { Input } from '@flowus/common/components/input';
import { googleFirebaseEvent } from '@flowus/common/google-firebase';
import { useGetCacheApi } from '@flowus/common/hooks/use-api-state';
import { PayType } from '@flowus/common/utils/pay-info';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { PlanCouponDto, PlanDTO } from '@next-space/fe-api-idl';
import { useDebounceEffect } from 'ahooks';
import dayjs from 'dayjs';
import { first, parseInt } from 'lodash-es';
import type { FC, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { createModel } from 'src/common/create-model';
import { request } from 'src/common/request';
import { useOpenBuildInPay } from 'src/components/stripe';
import { useFetchSpaceCapacity } from 'src/hooks/drive/use-fetch-space-capacity';
import { useCurrentSpace } from 'src/hooks/space';
import { Modals } from 'src/modals';
import { refreshSpaceData } from 'src/services/app/hook/refresh-space';
import { fetchAiCapacity } from 'src/services/capacity';
import { useUserBalance } from 'src/services/user/user-balance';
import { sleep } from 'src/utils/async-utils';
import { confetti } from 'src/utils/confetti';
import { getCurrencySymbols } from 'src/utils/currency-format';
import { OpenSettingFrom, SettingMenuType } from 'src/views/main/setting-modal/type';
import {
  useCloseSettingModal,
  useOpenSettingModal,
} from 'src/views/main/setting-modal/use-open-setting-modal';
import type { AsyncReturnType } from 'type-fest';
import { orderBizTracker } from '../common';
import { PayQrModal } from '../pay-qr-modal';

export interface UpgradeSupportParams {
  planId?: string;
  isFrom?: OpenSettingFrom;
}

export interface UpgradeSupportProps extends UpgradeSupportParams {
  upgradeType: 'ai' | 'capacity';
}

export const useUpgradeSupport = (props: UpgradeSupportProps) => {
  const { isFrom = OpenSettingFrom.dashboard_file, upgradeType } = props;
  const { integralInfo } = useUserBalance();
  const isUpgradeAi = upgradeType === 'ai';
  const { t } = useAppTranslation();

  // const isUpgradeCapacity = upgradeType === 'capacity';
  const openSettingModal = useOpenSettingModal();
  const closeSettingModal = useCloseSettingModal();
  const fetchSpaceCapacity = useFetchSpaceCapacity();
  const currentSpace = useCurrentSpace();
  const openModal = useOpenModal();
  const [payMethod, setPayMethod] = useState<PayType>(
    __BUILD_IN__ ? PayType.stripe : PayType.alipay
  );
  const openBuildInPay = useOpenBuildInPay();
  const [planId, setPlanId] = useState<string>(props.planId ?? '');
  const [planList, setPlanList] = useState<PlanDTO[]>([]);
  const getCacheQuote = useGetCacheQuote();
  const getCouponListApi = useGetCacheApi(request.infra.getCouponList);
  /** 后端计算的价格 */
  const [calculateQuote, setCalculateQuote] = useState<
    Record<string, AsyncReturnType<typeof request.infra.getQuote> & { planId: string }>
  >({});
  const [couponId, setCouponId] = useState<string>('');
  const [couponList, setCouponList] = useState<PlanCouponDto[]>([]);
  const [loading, setLoading] = useState(true);

  /** 当前套餐的价格 */
  const curCalculateQuote = calculateQuote[`${planId}${couponId}`];

  const currentPlan = planList.find((i) => i.id === planId);

  /** 关闭弹窗 */
  const close = () => {
    openModal.closeModal(Modals.UPGRADE_SUPPORT);
  };

  /** 更新优惠券列表 */
  const patchCouponList = async () => {
    if (!planId) return;
    const couponListRes = await getCouponListApi(planId, undefined, currentSpace.uuid);
    setCouponList(couponListRes.list ?? []);
    const firstCoupon = first(
      couponListRes.list?.filter(
        (i) => (i.integralRecord ?? 0) <= (integralInfo?.currentScore ?? 0)
      )
    );
    if (!firstCoupon) {
      selectCoupon('');
      return;
    }
    if (!couponListRes.list?.some((it) => it.id === couponId)) {
      selectCoupon(firstCoupon.id);
    }
  };

  /** 选择套餐 */
  const onSelectPlan = (id: string) => {
    setLoading(true);
    setPlanId(id);
  };

  /** 计算价格 */
  const patchQuote = async () => {
    if (planId && currentSpace.uuid) {
      const res = await getCacheQuote({
        spaceId: currentSpace.uuid,
        planId,
        couponId,
        date: 0,
        people: 0,
      });
      if (planId) {
        setCalculateQuote((pre) => ({ ...pre, [`${planId}${couponId}`]: { ...res, planId } }));
      }
    }
    setLoading(false);
  };

  /** 自动计算价格 */
  useDebounceEffect(
    () => {
      void patchQuote();
    },
    [planId, couponId],
    { wait: 100 }
  );

  // 发送订单埋点，抽出来是因为有三个地方使用
  const sendOrderBizTracker = (bizType: 'order_finish' | 'pay_order') => {
    const curPlan = planList.find((i) => i.id === planId);
    const trackParams = {
      order_channel: payMethod === PayType.alipay ? 'zhifubao' : 'stripe',
      is_from: isFrom,
      order_amount: curCalculateQuote?.amount,
      order_capacity: curPlan?.amount,
      space_type: currentSpace.planType,
      plan_id: planId,
      plan_name: curPlan?.name,
      coupon_id: couponId || undefined,
    };

    // 业务埋点
    orderBizTracker({
      bizType: `${bizType}_${isUpgradeAi ? 'ai' : 'file'}`,
      ...trackParams,
    });

    // Firebase 埋点 (仅 Buildin 产品生产环境)
    if (__BUILD_IN__ && __HOST_PRODUCTION__) {
      if (bizType === 'pay_order') {
        // 用户点击支付按钮
        googleFirebaseEvent('begin_checkout', {
          currency: curCalculateQuote?.amountUnit || 'USD',
          value: curCalculateQuote?.amount,
          items: [
            {
              item_id: planId,
              item_name: curPlan?.name,
              item_category: isUpgradeAi ? 'ai_plan' : 'capacity_plan',
              price: curCalculateQuote?.amount,
            },
          ],
          ...trackParams,
        });
      } else if (bizType === 'order_finish') {
        // 支付完成
        googleFirebaseEvent('purchase', {
          transaction_id: `${Date.now()}`,
          currency: curCalculateQuote?.amountUnit || 'USD',
          value: curCalculateQuote?.amount,
          items: [
            {
              item_id: planId,
              item_name: curPlan?.name,
              item_category: isUpgradeAi ? 'ai_plan' : 'capacity_plan',
              price: curCalculateQuote?.amount,
            },
          ],
          ...trackParams,
        });
      }
    }
  };

  /** 更新优惠券列表 */
  useEffect(() => {
    void patchCouponList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  /** 套餐到期时间 */
  const lastDate = useMemo(() => {
    if (!curCalculateQuote?.desiredExpireTime) return '';
    const lastDay = dayjs(curCalculateQuote?.desiredExpireTime || Date.now());
    return lastDay.format(t('common.date_format_ymd'));
  }, [curCalculateQuote?.desiredExpireTime, t]);

  const getClientSecret = () => {
    const payFn = __BUILD_IN__
      ? request.infra.createPreOrderV2
      : isUpgradeAi
      ? request.infra.createAiUsageOrder
      : request.infra.createCapacityOrder;

    // 获取 Rewardful 推荐人 ID（用于联盟营销佣金归因）
    let referralId: string | undefined;
    if (__BUILD_IN__ && typeof window !== 'undefined') {
      // 通过 Rewardful 全局对象获取 referral ID
      const rw = (window as any).Rewardful;
      if (rw && rw.referral) {
        referralId = rw.referral;
      }
    }

    return payFn({
      spaceId: currentSpace.uuid,
      planId,
      couponId,
      payType: payMethod,
      referralId, // 传递推荐人 ID 到后端
    });
  };

  /** 下单 */
  const onPay = async () => {
    message.loading(t('components.select_upgrade_plan.creating_order'));
    setLoading(true);

    sendOrderBizTracker('pay_order');
    const order = await getClientSecret();

    setLoading(false);
    if (!order.orderId) {
      return message.error(t('components.select_upgrade_plan.order_failed_retry'));
    }

    if (__BUILD_IN__) {
      openBuildInPay(order);
      return;
    }

    openModal.modal({
      modalId: Modals.PAY_MODAL,
      content: () => (
        <PayQrModal
          rePayOrder={getClientSecret}
          onFinish={async () => {
            sendOrderBizTracker('order_finish');
            close();
            closeSettingModal();
            if (isUpgradeAi) {
              await fetchAiCapacity();
            } else {
              await fetchSpaceCapacity();
            }
            await sleep(500);
            openSettingModal({
              defaultMenuType: SettingMenuType.upgrade,
              from: OpenSettingFrom.pay,
            });
          }}
          payType={payMethod}
          priceTitle={`${getCurrencySymbols(curCalculateQuote?.amountUnit)}${order.amount}`}
          orderId={`${order.orderId}`}
          qrCodeUrl={`${order.qrCodeUrl}`}
        />
      ),
    });
  };

  /** 选择优惠券 */
  const selectCoupon = (id = '') => {
    setCouponId(id);
  };

  /** 套餐列表 */
  useEffect(() => {
    const api = isUpgradeAi ? request.infra.aiUsagePlanList : request.infra.capacityPlanList;

    void api(currentSpace.uuid).then((res) => {
      const list = res.list ?? [];
      const [first] = list;
      if (first) {
        setPlanList(list);
        if (!planId || !list.map((i) => i.id).includes(planId)) {
          setPlanId(first.id);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpace.uuid]);

  return {
    currentPlan,
    getClientSecret,
    couponId,
    selectCoupon,
    onPay,
    close,
    payMethod,
    setPayMethod,
    planId,
    onSelectPlan,
    planList,
    couponList,
    curCalculateQuote,
    loading,
    lastDate,
  };
};

export const useGetCacheQuote = () => {
  return useGetCacheApi(request.infra.getQuote);
};

export const createUpgradeSupportModel = () => createModel(useUpgradeSupport);

export const getPlanList = __BUILD_IN__
  ? request.infra.overseaPlanList
  : request.infra.getNewPlanList;

export const useStripeSuccess = () => {
  const { t } = useAppTranslation();
  return useCallback(async () => {
    message.success(t('common.payment_success'));

    // Firebase 埋点 - Stripe 支付成功 (仅 Buildin 产品生产环境)
    if (__BUILD_IN__ && __HOST_PRODUCTION__) {
      googleFirebaseEvent('stripe_payment_success', {
        payment_method: 'stripe',
      });
    }
    void confetti.fireworks();
    await sleep(3000);
    await refreshSpaceData();
  }, [t]);
};

// #region 输入框组件
interface CustomNumberInputProps extends InputProps<'number'> {
  value?: number;
  afterText: ReactNode;
  readonly?: boolean;
}
export const CustomNumberInput: FC<CustomNumberInputProps> = memo((props) => {
  const { value, afterText, inputClassName, onChange, ...reset } = props;
  const { t } = useAppTranslation();
  return (
    <Input
      focusHiddenPlaceholder
      inputType="number"
      placeholder={t('components.select_upgrade_plan.custom')}
      showBorder={false}
      value={value || ''}
      inputClassName={cx('h-9 text-center', inputClassName)}
      onChange={(num) => {
        const str = Math.max(0, Number(parseInt(`${num}`)));
        onChange?.(Number.isNaN(str) ? 0 : str);
      }}
      addonAfter={
        <>
          <span className="h-4 w-px bg-black_006" />
          <span className="flex h-full min-w-10 px-2 items-center justify-center">{afterText}</span>
        </>
      }
      {...reset}
    />
  );
});
// #endregion
